import { supabase } from './supabase';

// ─── Host-to-host matching engine ─────────────────────────────────────────
// Given a user's profile, score every other podcaster in the database on a
// 0-100 compatibility scale and surface the top matches. Rationale:
//
//   • Same niche means audience overlap → guest appearances convert.
//   • Similar keywords in the bio/show mean topical fit beyond label matches.
//   • Audience-size compatibility matters on both sides: a 100× mismatch
//     usually doesn't happen; a 3× gap is the sweet spot.
//   • "Open to guests" = they're actively looking, so prioritize.
//   • Recent profile activity proxies for "this person actually uses the app."
//
// All heuristics — no ML. Replace with a proper embedding model once we have
// enough profile volume.

const STOPWORDS = new Set([
  'the','and','for','with','that','this','from','about','your','you','our',
  'are','but','not','have','has','will','can','all','any','a','an','of','to',
  'in','on','is','it','as','at','by','be','or','if','we','i','my','me','us',
  'their','they','them','his','her','he','she','what','how','why','when',
  'where','who','podcast','show','episode','episodes','weekly','daily',
]);

function tokens(text = '') {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 2 && !STOPWORDS.has(w));
}

function setFrom(text) { return new Set(tokens(text)); }

/**
 * Score a single pairing. Returns { score: 0-100, reasons: string[] }.
 * `mine` and `theirs` are podcaster_profiles rows. The score captures
 * compatibility for BOTH directions (guest ↔ host) — we don't differentiate
 * who pitches whom; the UI does.
 */
export function scoreMatch(mine = {}, theirs = {}) {
  let score = 0;
  const reasons = [];

  // ── Niche (40 pts) ──────────────────────────────────────────────────────
  const mn = (mine.niche || '').trim().toLowerCase();
  const tn = (theirs.niche || '').trim().toLowerCase();
  if (mn && tn) {
    if (mn === tn) {
      score += 40;
      reasons.push(`Same niche · ${theirs.niche}`);
    } else {
      const a = setFrom(mn);
      const b = setFrom(tn);
      const overlap = [...a].filter(w => b.has(w)).length;
      if (overlap > 0) {
        const pts = Math.min(25, overlap * 12);
        score += pts;
        reasons.push('Related niche');
      }
    }
  }

  // ── Bio / show topic overlap (25 pts) ───────────────────────────────────
  const myText    = setFrom([mine.bio, mine.show_name, mine.niche].filter(Boolean).join(' '));
  const theirText = setFrom([theirs.bio, theirs.show_name, theirs.niche].filter(Boolean).join(' '));
  const shared    = [...myText].filter(w => theirText.has(w));
  if (shared.length > 0) {
    const pts = Math.min(25, shared.length * 3);
    score += pts;
    if (shared.length >= 3) {
      reasons.push(`${shared.length} shared topics`);
    }
  }

  // ── Audience fit (15 pts) ───────────────────────────────────────────────
  const ma = Number(mine.avg_listeners) || 0;
  const ta = Number(theirs.avg_listeners) || 0;
  if (ma > 0 && ta > 0) {
    const ratio = Math.max(ma, ta) / Math.max(1, Math.min(ma, ta));
    if (ratio < 3)       { score += 15; reasons.push('Similar audience size'); }
    else if (ratio < 10) { score += 10; }
    else if (ratio < 50) { score += 5; }
  } else if (ma === 0 && ta === 0) {
    // Both starting out — treat as compatible (they grow together).
    score += 8;
  }

  // ── Open to guests (10 pts) ─────────────────────────────────────────────
  if (theirs.open_to_guests) {
    score += 10;
    reasons.push('Open to guests');
  }

  // ── Recent activity (10 pts) ────────────────────────────────────────────
  if (theirs.updated_at) {
    const daysAgo = (Date.now() - new Date(theirs.updated_at).getTime()) / 86400000;
    if (daysAgo < 14) {
      score += 10;
      if (daysAgo < 3) reasons.push('Active this week');
    } else if (daysAgo < 45) {
      score += 4;
    }
  }

  // ── Has an actual show name (5 pts) — filters out profiles that are
  // bare accounts. ────────────────────────────────────────────────────────
  if ((theirs.show_name || '').trim()) score += 5;

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    reasons,
    // The actual overlapping topics (words shared across bio / show / niche),
    // surfaced so the card can show them in a "shared topics" dropdown. Longer
    // words only (drop 1-3 char noise) and capped so the list stays tight.
    sharedTopics: shared.filter((w) => w && w.length >= 4).slice(0, 10),
  };
}

/**
 * Given the current user's profile and a pool of other profiles, return
 * the top N scored matches. The caller is responsible for fetching the pool
 * (see fetchMatchPool below).
 */
export function findMatches(me, pool, { limit = 12, minScore = 20 } = {}) {
  if (!me) return [];
  const scored = [];
  for (const other of (pool || [])) {
    if (!other?.user_id || other.user_id === me.user_id) continue;
    const { score, reasons, sharedTopics } = scoreMatch(me, other);
    if (score < minScore) continue;
    scored.push({ profile: other, score, reasons, sharedTopics });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

/**
 * Fetch the pool of other podcasters we should score against. Excludes
 * the current user. Deliberately wide (100 rows) so matching quality
 * isn't capped by SELECT order.
 */
// Cached: getTopMatches runs twice on the home alone (HomeMetrics +
// HomeDiscovery). A short-lived cache keyed by viewer + in-flight dedupe means
// those share one query instead of each hitting Supabase on every home load.
const POOL_TTL = 60_000;
let _poolCache = { at: 0, uid: null, data: null, promise: null };

export async function fetchMatchPool(currentUserId) {
  if (!supabase) return [];
  const now = Date.now();
  if (_poolCache.uid === currentUserId && _poolCache.data && (now - _poolCache.at) < POOL_TTL) {
    return _poolCache.data;
  }
  if (_poolCache.promise && _poolCache.uid === currentUserId) {
    return _poolCache.promise;
  }
  const promise = (async () => {
    const { data, error } = await supabase
      .from('podcaster_profiles')
      .select('user_id, full_name, show_name, username, niche, bio, avatar_url, avg_listeners, episodes_created, open_to_guests, verified, updated_at')
      .neq('user_id', currentUserId)
      .order('updated_at', { ascending: false })
      .limit(100);
    if (error) { console.error('fetchMatchPool error:', error); return []; }
    return data || [];
  })();
  _poolCache = { at: now, uid: currentUserId, data: null, promise };
  try {
    const data = await promise;
    _poolCache = { at: Date.now(), uid: currentUserId, data, promise: null };
    return data;
  } catch {
    _poolCache = { at: 0, uid: null, data: null, promise: null };
    return [];
  }
}

// ── Placeholder creators ────────────────────────────────────────────────────
// Demo profiles that top up the deck/grid while the real network is small, so
// matching + swiping are testable today. They carry no real user_id (prefix
// "demo:"), so swiping them is local-only (recordSwipe fails soft) until real
// creators fill the pool. Remove the top-up once the network is dense.
const PLACEHOLDER_CREATORS = [
  { user_id: 'demo:maya',   full_name: 'Maya Okonkwo',   username: 'mayatalks',    show_name: 'The Quiet Ambition',   niche: 'Self Improvement', bio: 'Slow productivity, deep work, and building a life that doesn’t need a vacation.',    avg_listeners: 4200,  episodes_created: 58,  open_to_guests: true,  verified: true,  account_type: 'host' },
  { user_id: 'demo:devon',  full_name: 'Devon Reyes',    username: 'devonreyes',   show_name: 'Founder Mode',          niche: 'Entrepreneurship', bio: 'Unfiltered founder stories — the raises, the layoffs, the 3am pivots.',              avg_listeners: 9800,  episodes_created: 122, open_to_guests: true,  verified: true,  account_type: 'host' },
  { user_id: 'demo:aisha',  full_name: 'Aisha Bello',    username: 'aishabello',   show_name: 'Heart Work',            niche: 'Mental Health',    bio: 'Therapy-informed conversations about burnout, boundaries, and rest.',                avg_listeners: 2600,  episodes_created: 31,  open_to_guests: true,  verified: false, account_type: 'host' },
  { user_id: 'demo:theo',   full_name: 'Theo Marsh',     username: 'theomarsh',    show_name: null,                    niche: 'Author / Guest',   bio: 'Author of “Make It Mean Something.” Looking to guest on growth + creativity shows.', avg_listeners: 0,     episodes_created: 0,   open_to_guests: true,  verified: false, account_type: 'guest' },
  { user_id: 'demo:lina',   full_name: 'Lina Park',      username: 'linapark',     show_name: 'Built Different',       niche: 'Health & Fitness', bio: 'Strength training for people who hate the gym. Science, not bro-science.',           avg_listeners: 6100,  episodes_created: 77,  open_to_guests: false, verified: true,  account_type: 'host' },
  { user_id: 'demo:marcus', full_name: 'Marcus Hale',    username: 'marcushale',   show_name: 'Money Unscripted',      niche: 'Finance',          bio: 'Personal finance without the shame. Debt, investing, and real numbers.',             avg_listeners: 14500, episodes_created: 210, open_to_guests: true,  verified: true,  account_type: 'host' },
  { user_id: 'demo:sora',   full_name: 'Sora Nakamura',  username: 'soracreates',  show_name: 'Make / Ship / Repeat',  niche: 'Creativity',       bio: 'Indie makers on the messy middle of building in public.',                            avg_listeners: 3300,  episodes_created: 45,  open_to_guests: true,  verified: false, account_type: 'host' },
  { user_id: 'demo:brandx', full_name: 'Lumen Coffee',   username: 'lumencoffee',  show_name: null,                    niche: 'Brand / Sponsor',  bio: 'Specialty coffee brand looking to sponsor growth + wellness podcasts.',              avg_listeners: 0,     episodes_created: 0,   open_to_guests: false, verified: true,  account_type: 'brand' },
  { user_id: 'demo:jess',   full_name: 'Jess Romano',    username: 'jessromano',   show_name: 'The Side Door',         niche: 'Storytelling',     bio: 'First-person stories from people you’d never meet otherwise.',                       avg_listeners: 5400,  episodes_created: 64,  open_to_guests: true,  verified: true,  account_type: 'host' },
  { user_id: 'demo:omar',   full_name: 'Omar Haddad',    username: 'omarhaddad',   show_name: 'Deep End',              niche: 'Technology',       bio: 'Long-form interviews with the people quietly shaping the internet.',                 avg_listeners: 8700,  episodes_created: 99,  open_to_guests: true,  verified: false, account_type: 'host' },
  { user_id: 'demo:nadia',  full_name: 'Nadia Cole',     username: 'nadiacole',    show_name: null,                    niche: 'Author / Guest',   bio: 'Memoirist + speaker. Available to guest on identity, family, and reinvention.',      avg_listeners: 0,     episodes_created: 0,   open_to_guests: true,  verified: false, account_type: 'guest' },
  { user_id: 'demo:rae',    full_name: 'Rae Lindqvist',  username: 'raelindqvist', show_name: 'Northern Light',        niche: 'Spirituality',     bio: 'Calm, curious talks on meaning, ritual, and modern faith.',                          avg_listeners: 3900,  episodes_created: 52,  open_to_guests: true,  verified: true,  account_type: 'host' },
];
const MIN_DECK = 8; // top up to at least this many cards so the deck feels alive

export function isPlaceholderMatch(m) {
  return typeof m?.profile?.user_id === 'string' && m.profile.user_id.startsWith('demo:');
}

/**
 * Convenience: fetch + score in one call. Returns top matches ready to
 * render. Tops up with demo creators while the real pool is thin so the
 * deck/grid is always testable (see PLACEHOLDER_CREATORS).
 */
export async function getTopMatches(myProfile, opts = {}) {
  if (!myProfile?.user_id) return [];
  const pool = await fetchMatchPool(myProfile.user_id);
  const real = findMatches(myProfile, pool, opts);
  if (real.length >= MIN_DECK) return real;

  const realIds = new Set(real.map((m) => m.profile.user_id));
  const fillers = findMatches(myProfile, PLACEHOLDER_CREATORS, { ...opts, minScore: 0 })
    .filter((m) => !realIds.has(m.profile.user_id));
  return [...real, ...fillers].slice(0, opts.limit || 24);
}

/**
 * Storage key for "matches we've already notified this user about." Used by
 * the activity seeder so we don't re-notify on every reload.
 */
export const SEEN_MATCHES_KEY = (userId) => `episode_seen_matches_${userId || 'guest'}`;
